import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/** PD-17109 — the kinds of record a merge with Undo exists for. */
export const RECORD_MERGE_ENTITY_TYPES = ['job', 'opportunity'] as const;

export type RecordMergeEntityType = (typeof RECORD_MERGE_ENTITY_TYPES)[number];

/**
 * PD-17109 — how long after a merge its Undo still works. A week, not the
 * visit debrief's day: a duplicate job or opportunity is often noticed only
 * when someone opens it again. Undo only moves back the rows the merge moved,
 * so work added to the kept record in the meantime stays where it is.
 */
export const RECORD_MERGE_UNDO_DAYS = 7;

/**
 * What a merge did to one child table (orders on a job, people on an
 * opportunity, ...), so Undo can put it back.
 */
export interface RecordMergeChildUndoModel {
	/** Rows that moved from the merged record to the kept one. */
	movedIds: Array<number>;
	/** Merged rows removed because the kept record already had the same one. */
	removedIds: Array<number>;
	/**
	 * Kept rows that had been removed and were brought back to take a
	 * duplicate's place (the table's unique index counts removed rows, so the
	 * merged row could not move onto the kept record).
	 */
	revivedIds: Array<number>;
	/** Earlier values of rows the merge changed in place, restored on Undo. */
	before: Array<{ id: number; values: Record<string, unknown> }>;
}

/** Everything a merge changed, so its Undo can put it all back. */
export interface RecordMergeUndoModel {
	/** Fields of the kept record filled from the merged one: the value before and after. */
	fields: Record<string, { before: unknown; after: unknown }>;
	/** Child rows by table key (see the merge's child table list). */
	children: Record<string, RecordMergeChildUndoModel>;
	/**
	 * An imported opportunity's outside id (SalesNOW and the like). The kept
	 * record keeps its own; the merged one's is recorded here because a
	 * re-import that matches by it would no longer find the merged record.
	 */
	mergedExternalId?: string | null;
}

/**
 * PD-17109 — one merge of a duplicate job or opportunity into the record kept,
 * with what it changed. Kept on the server rather than handed to the browser
 * (the visit debrief pattern), so an Undo can only reverse what that merge did
 * and works from the page's toast and from the Sales Helper alike. Undo works
 * once, for RECORD_MERGE_UNDO_DAYS, for anyone allowed to merge that kind of
 * record who may open the kept one.
 *
 * SM1 exposure: none (a new table).
 */
@Index('record_merge__kept', ['entityType', 'keptEntityId'], {})
@Index('record_merge__merged', ['entityType', 'mergedEntityId'], {})
@Entity('record_merge')
export class RecordMergeEntity extends BaseEntity {
	@Column('varchar', { name: 'entityType', length: 32 })
	public entityType!: RecordMergeEntityType;

	/** The record kept (its `_id`). */
	@Column('int', { name: 'keptEntityId', unsigned: true })
	public keptEntityId!: number;

	/** The record merged into it and removed (its `_id`). */
	@Column('int', { name: 'mergedEntityId', unsigned: true })
	public mergedEntityId!: number;

	@Column('json', { name: 'undoRecord' })
	public undo!: RecordMergeUndoModel;

	@Column('datetime', { name: 'undoneDate', nullable: true })
	public undoneDate!: Date | null;
}
